import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Table, Form, Alert, Row, Col } from "react-bootstrap";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { colors, constants, generateRoutePath } from "utils";
import { useAuthUserData } from "hooks/useAuthUserData/useAuthUserData";
import { request } from "hooks/useOkoApi/useOkoApi";

const PageContainer = styled.div`
  max-width: 1200px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const PageTitle = styled.h2`
  margin: 0;
  color: ${colors.nightlyWoods900};
`;

const FiltersContainer = styled.div`
  margin-bottom: 16px;
`;

const StyledTable = styled(Table)`
  background: white;
  border-radius: 8px;
  overflow: hidden;

  th {
    background: ${colors.milkGlass400};
    border-bottom: none;
    font-weight: 600;
    color: ${colors.nightlyWoods700};
  }

  td {
    vertical-align: middle;
  }

  tbody tr {
    cursor: pointer;
    &:hover {
      background: ${colors.milkGlass200};
    }
  }
`;

const RoleTag = styled.span`
  background: ${props => props.variant === "admin" ? colors.orange100 : colors.blue100};
  color: ${props => props.variant === "admin" ? colors.orange700 : colors.blue700};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  text-transform: uppercase;
  margin-right: 4px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
`;

const ROLE_OPTIONS = [
  { value: "", label: "All Roles" },
  { value: "superuser", label: "Superuser" },
  { value: "oko_staff", label: "OKO Staff" },
  { value: "client_admin", label: "Client Admin" },
  { value: "school_admin", label: "School Admin" },
  { value: "content_manager", label: "Content Manager" },
  { value: "educator", label: "Educator" },
  { value: "student", label: "Student" },
];

const EDUCATOR_TYPE_OPTIONS = [
  { value: "", label: "All Types" },
  { value: "teacher", label: "Teacher" },
  { value: "math_specialist", label: "Math Specialist" },
  { value: "school_administrator", label: "School Administrator" },
  { value: "district_administrator", label: "District Administrator" },
];

export const UserList = () => {
  const { authUserData, loading: userLoading } = useAuthUserData();
  const history = useHistory();
  const location = useLocation();

  const getInitialParams = () => {
    const params = new URLSearchParams(location.search);
    return {
      search: params.get("search") || "",
      role: params.get("role") || "",
      educatorType: params.get("educatorType") || "",
      client: params.get("client") || "",
      page: parseInt(params.get("page"), 10) || 1,
    };
  };

  const initialParams = getInitialParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState(initialParams.search);
  const [roleFilter, setRoleFilter] = useState(initialParams.role);
  const [educatorTypeFilter, setEducatorTypeFilter] = useState(initialParams.educatorType);
  const [clientFilter, setClientFilter] = useState(initialParams.client);
  const [clients, setClients] = useState([]);
  const [page, setPage] = useState(initialParams.page);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set("search", searchTerm);
    if (roleFilter) params.set("role", roleFilter);
    if (educatorTypeFilter) params.set("educatorType", educatorTypeFilter);
    if (clientFilter) params.set("client", clientFilter);
    if (page > 1) params.set("page", page.toString());

    const newSearch = params.toString();
    const currentSearch = location.search.replace(/^\?/, "");
    if (newSearch !== currentSearch) {
      history.replace({ pathname: location.pathname, search: newSearch ? `?${newSearch}` : "" });
    }
  }, [searchTerm, roleFilter, educatorTypeFilter, clientFilter, page]);

  useEffect(() => {
    if (userLoading) return;

    if (!authUserData || authUserData.isAnonymous) {
      history.push(generateRoutePath(constants.ADMIN_LOGIN_ROUTE_NAME));
      return;
    }

    fetchClients();

    if (!hasFetched) {
      fetchUsers();
    }
  }, [authUserData, userLoading, hasFetched]);

  const fetchClients = async () => {
    try {
      const response = await request("admin/clients", { method: "GET" });
      setClients(response.clients || []);
    } catch (err) {
      console.error("Failed to fetch clients:", err);
    }
  };

  useEffect(() => {
    if (hasFetched) {
      fetchUsers();
    }
  }, [roleFilter, educatorTypeFilter, clientFilter, page]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    setHasFetched(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("limit", limit.toString());
      if (roleFilter) params.append("role", roleFilter);
      if (educatorTypeFilter) params.append("educator_type", educatorTypeFilter);
      if (clientFilter) params.append("client_id", clientFilter);
      if (searchTerm) params.append("search", searchTerm);

      const response = await request(`admin/users?${params.toString()}`, { method: "GET" });
      setUsers(response.users || []);
      setTotal(response.total || 0);
    } catch (err) {
      console.error("Failed to fetch users:", err);
      if (err.status === 403) {
        setError("You do not have permission to view users.");
      } else {
        setError("Failed to load users. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (userId) => {
    history.push({
      pathname: generateRoutePath(constants.USER_ADMIN_DETAIL_ROUTE_NAME, { id: userId }),
      state: { listQuery: location.search },
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  const formatRole = (role) => {
    const found = ROLE_OPTIONS.find(r => r.value === role);
    return found ? found.label : role;
  };

  const isAdminRole = (role) => {
    return ["superuser", "oko_staff", "client_admin", "school_admin"].includes(role);
  };

  const totalPages = Math.ceil(total / limit);

  if (userLoading) {
    return (
      <LoadingContainer>
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
      </LoadingContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Users</PageTitle>
      </PageHeader>

      <FiltersContainer>
        <Form onSubmit={handleSearch}>
          <Row className="mb-2">
            <Col md={3}>
              <Form.Select
                value={clientFilter}
                onChange={(e) => { setClientFilter(e.target.value); setPage(1); }}
              >
                <option value="">All Clients</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Control
                type="text"
                placeholder="Search by name, email, or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Select
                value={roleFilter}
                onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
              >
                {ROLE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                value={educatorTypeFilter}
                onChange={(e) => { setEducatorTypeFilter(e.target.value); setPage(1); }}
              >
                {EDUCATOR_TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Button type="submit" variant="secondary" style={{ width: "100%" }}>
                Search
              </Button>
            </Col>
          </Row>
        </Form>
      </FiltersContainer>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <LoadingContainer>
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        </LoadingContainer>
      ) : (
        <>
          <StyledTable hover>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>School</th>
                <th>Roles</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center", padding: "40px" }}>
                    No users found.
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} onClick={() => handleRowClick(user.id)}>
                    <td><strong>{user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.display_name || user.oko_public_id || "-"}</strong></td>
                    <td>{user.email || "-"}</td>
                    <td>{user.school_name || "-"}</td>
                    <td>
                      {user.roles?.map(role => (
                        <RoleTag key={role} variant={isAdminRole(role) ? "admin" : "default"}>
                          {formatRole(role)}
                        </RoleTag>
                      ))}
                    </td>
                    <td>{formatDate(user.created_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </StyledTable>

          <PaginationContainer>
            <span>Showing {users.length} of {total} users</span>
            <div>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
                style={{ marginRight: "8px" }}
              >
                Previous
              </Button>
              <span style={{ margin: "0 8px" }}>Page {page} of {totalPages || 1}</span>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </Button>
            </div>
          </PaginationContainer>
        </>
      )}
    </PageContainer>
  );
};
